<template>
  <div id="past-events" class="jf-past section padding-top-65 padding-bottom-65">
    <div class="container">
      <div class="row">
        <div class="col-xl-12">
          <div class="section-headline centered margin-bottom-45">
            <h3>{{ pastEvents.title }}</h3>
            <span>A look back at our 3rd annual job fair</span>
          </div>
        </div>
      </div>
      <div class="row">
        <div class="col-xl-12">
          <div class="jf-past__video">
            <iframe
              src="https://www.youtube.com/embed/9yi9HIHCjMY"
              title="Macher Job Fair 2025"
              frameborder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowfullscreen
            ></iframe>
          </div>
        </div>
      </div>
      <div class="row">
        <div class="col-xl-12">
          <div class="jf-past__grid">
            <div
              class="jf-past__item"
              :class="{ 'jf-past__item--large': i === 0 || i === 5 }"
              v-for="(item, i) in pastEvents.photos"
              :key="i"
              @click="openLightbox(i)"
            >
              <img :src="item.image" :alt="pastEvents.title" loading="lazy">
              <div class="jf-past__overlay">
                <i class="icon-feather-zoom-in"></i>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Lightbox -->
    <div class="jf-lightbox" v-if="lightboxOpen" @click.self="closeLightbox">
      <button class="jf-lightbox__close" @click="closeLightbox"><i class="icon-feather-x"></i></button>
      <button class="jf-lightbox__prev" @click="prevPhoto"><i class="icon-feather-chevron-left"></i></button>
      <div class="jf-lightbox__img">
        <img :src="pastEvents.photos[lightboxIndex].image" :alt="pastEvents.title">
      </div>
      <button class="jf-lightbox__next" @click="nextPhoto"><i class="icon-feather-chevron-right"></i></button>
      <div class="jf-lightbox__counter">{{ lightboxIndex + 1 }} / {{ pastEvents.photos.length }}</div>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    pastEvents: { type: Object, required: true }
  },
  data() {
    return {
      lightboxOpen: false,
      lightboxIndex: 0
    };
  },
  methods: {
    openLightbox(i) {
      this.lightboxIndex = i;
      this.lightboxOpen = true;
      document.body.style.overflow = 'hidden';
    },
    closeLightbox() {
      this.lightboxOpen = false;
      document.body.style.overflow = '';
    },
    nextPhoto() {
      this.lightboxIndex = (this.lightboxIndex + 1) % this.pastEvents.photos.length;
    },
    prevPhoto() {
      this.lightboxIndex = (this.lightboxIndex - 1 + this.pastEvents.photos.length) % this.pastEvents.photos.length;
    },
    handleKeydown(e) {
      if (!this.lightboxOpen) return;
      if (e.key === 'Escape') this.closeLightbox();
      if (e.key === 'ArrowRight') this.nextPhoto();
      if (e.key === 'ArrowLeft') this.prevPhoto();
    }
  },
  mounted() {
    window.addEventListener('keydown', this.handleKeydown);
  },
  beforeDestroy() {
    window.removeEventListener('keydown', this.handleKeydown);
    document.body.style.overflow = '';
  }
};
</script>

<style lang="scss" scoped>
.jf-past {
  background: #f9f9f9;

  &__video {
    position: relative;
    padding-bottom: 56.25%;
    height: 0;
    overflow: hidden;
    border-radius: 10px;
    margin-bottom: 40px;

    iframe {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
    }
  }

  &__grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 12px;
  }

  &__item {
    position: relative;
    border-radius: 8px;
    overflow: hidden;
    cursor: pointer;
    height: 220px;

    &--large {
      grid-column: span 2;
      height: 280px;
    }

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      transition: transform 0.3s;
    }

    &:hover img {
      transform: scale(1.05);
    }
  }

  &__overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(42, 65, 232, 0.3);
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0;
    transition: opacity 0.3s;

    i {
      font-size: 28px;
      color: #fff;
    }
  }

  &__item:hover &__overlay {
    opacity: 1;
  }
}

/* Lightbox */
.jf-lightbox {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.92);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;

  &__img {
    max-width: 85vw;
    max-height: 85vh;

    img {
      max-width: 100%;
      max-height: 85vh;
      object-fit: contain;
      border-radius: 4px;
    }
  }

  &__close,
  &__prev,
  &__next {
    position: absolute;
    background: none;
    border: none;
    color: #fff;
    font-size: 28px;
    cursor: pointer;
    padding: 15px;
    opacity: 0.7;
    transition: opacity 0.2s;

    &:hover {
      opacity: 1;
    }
  }

  &__close {
    top: 20px;
    right: 20px;
  }

  &__prev {
    left: 20px;
    top: 50%;
    transform: translateY(-50%);
  }

  &__next {
    right: 20px;
    top: 50%;
    transform: translateY(-50%);
  }

  &__counter {
    position: absolute;
    bottom: 25px;
    left: 50%;
    transform: translateX(-50%);
    color: rgba(255, 255, 255, 0.7);
    font-size: 14px;
  }
}

@media (max-width: 768px) {
  .jf-past__grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .jf-past__item {
    height: 160px;

    &--large {
      grid-column: span 1;
      height: 160px;
    }
  }
}

@media (max-width: 480px) {
  .jf-past__grid {
    grid-template-columns: 1fr;
  }
}
</style>
